import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/** @type {Promise<{ code: string; applicable: boolean }[]> | null} */
let cartCodesRead = null;
let cartCodesReadAt = 0;

/**
 * The basket's discount codes from /cart.js. The basket page and the drawer
 * share one read; `fresh` reads again after a change, unless a read started
 * in the last half second (both instances hear the same event).
 * @param {boolean} [fresh]
 */
function readCartCodes(fresh = false) {
  if (!cartCodesRead || (fresh && Date.now() - cartCodesReadAt > 500)) {
    cartCodesReadAt = Date.now();
    cartCodesRead = fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => (Array.isArray(cart?.discount_codes) ? cart.discount_codes : []))
      .catch(() => []);
  }
  return cartCodesRead;
}

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Puts the chip back after any re-render, including late ones no event announces. */
  #chipWatch = new MutationObserver(() => this.#showCodesFromCart(true));

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartChange);
    this.#chipWatch.observe(this, { childList: true, subtree: true });
    this.#showCodesFromCart(true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#onCartChange);
    this.#chipWatch.disconnect();
  }

  /**
   * After a basket or code change re-renders this block, put the shipping
   * code's chip back (the event's promise settles before the re-render).
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #onCartChange = (event) => {
    event.promise?.then(() => this.#showCodesFromCart(true)).catch(() => {});
  };

  /**
   * D2 follow-up (31 Oct 2026): after a reload, or a re-render, a shipping
   * code (FREESHIP) has no chip, because the server render cannot list it.
   * Without the chip, applying another code sends only that code and Shopify
   * drops FREESHIP. Read the codes from the basket and put the chip back.
   * Adding the chip wakes the observer once more; the chip is then there, so
   * it stops (and the read is shared for half a second).
   * @param {boolean} [fresh]
   */
  async #showCodesFromCart(fresh = false) {
    const codes = await readCartCodes(fresh);
    if (!this.isConnected) return;
    const shown = this.#existingDiscounts().map((code) => code.toUpperCase());
    for (const discount of codes) {
      if (discount?.applicable !== true || typeof discount.code !== 'string' || !discount.code) continue;
      if (!shown.includes(discount.code.toUpperCase())) {
        this.#showShippingCodeApplied(discount.code);
      }
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return;

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          // D2 (31 Oct 2026, Rogue Alloy): a shipping code (FREESHIP) is
          // applied and applicable but has no allocation in the basket, so
          // the server render has no chip for it. That is a success, not an
          // error: render the section, add the chip and say what happens.
          discountCode.value = '';
          deferredPromise.resolve({
            cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          });
          morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
          this.#showShippingCodeApplied(discountCodeValue);
          return;
        }
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      discountCode.value = '';
      morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId], {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  /**
   * D2 (31 Oct 2026): the chip and the success line for a shipping code
   * the server render cannot list (it has no allocation in the basket).
   * The chip's remove button uses the same on:click as the server's chips,
   * which the component binds by delegation.
   * @param {string} code
   */
  #showShippingCodeApplied(code) {
    const list = this.querySelector('.cart-discount__codes');
    if (!list || list.querySelector(`[data-discount-code="${CSS.escape(code)}"]`)) return;
    const pill = document.createElement('li');
    pill.className = 'cart-discount__pill cart-discount__pill--shipping';
    pill.dataset.discountCode = code;
    pill.setAttribute('aria-label', `${code} applied`);
    const text = document.createElement('p');
    text.className = 'cart-discount__pill-code';
    text.textContent = code;
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.setAttribute('on:click', '/removeDiscount');
    remove.className = 'cart-discount__pill-remove svg-wrapper svg-wrapper--smaller button-unstyled';
    remove.setAttribute('aria-label', `Remove ${code}`);
    const existingRemove = this.querySelector('.cart-discount__pill-remove svg');
    if (existingRemove) remove.append(existingRemove.cloneNode(true));
    else remove.textContent = '×';
    pill.append(text, remove);
    list.append(pill);
    const upper = code.toUpperCase();
    const note = document.createElement('p');
    note.className = 'cart-discount__applied';
    note.setAttribute('role', 'status');
    note.textContent = `${code} added. ${upper === 'FREESHIP' ? 'Free delivery shows at checkout.' : 'Your delivery discount shows at checkout.'}`;
    list.insertAdjacentElement('afterend', note);
    const details = this.closest('details');
    if (details) details.open = true;
  }

  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
